/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDirectDialData.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for loading Direct Dial data.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchDirectDialById } from '../api/directDialApi';
import { createDefaultFormData } from '../utils/formDefaults';
import type { DirectDialFormState } from '../types';

interface UseDirectDialDataOptions {
  id?: string;
  duplicateId?: string | null;
  isEditMode: boolean;
}

export function useDirectDialData({
  id,
  duplicateId,
  isEditMode,
}: UseDirectDialDataOptions) {
  const location = useLocation();
  const [formData, setFormData] = useState<DirectDialFormState>(createDefaultFormData);
  const [isLoading, setIsLoading] = useState(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');

  const loadDirectDial = useCallback(async (loadId: string, duplicate: boolean = false) => {
    setIsLoading(true);
    try {
      const application = await fetchDirectDialById(loadId);

      const loadedData: DirectDialFormState = {
        code: application.code || '',
        name: application.name || '',
        description: application.description || '',
        context: application.context || '',
        destinationModule: application.destinationModule || 'extension',
        destinationValue: application.destinationValue || '',
        enabled: application.enabled ?? true
      };

      const finalData = duplicate
        ? {
            ...loadedData,
            code: '',
            name: loadedData.name ? `${loadedData.name} Copy`.trim() : loadedData.name
          }
        : loadedData;

      setFormData(finalData);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadId = duplicateId || id;
    const savedDataKey = 'directDial:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (!loadId || loadId === 'new') {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ? 
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;
      
      const shouldReset = lastLoadedIdRef.current !== 'new' || 
                          lastLocationKeyRef.current !== locationKey ||
                          (lastLoadedIdRef.current === 'new' && loadId === 'new' && 
                           resetTimestamp && resetTimestamp !== lastResetTimestamp);
      
      if (shouldReset) {
        // Clear any saved data to ensure fresh form
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }
        
        // Reset form to defaults
        setFormData(createDefaultFormData());
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;
      }
      return;
    }

    if (lastLoadedIdRef.current !== loadId) {
      lastLoadedIdRef.current = loadId;
      lastLocationKeyRef.current = locationKey;
      loadDirectDial(loadId, Boolean(duplicateId)).catch(() => {
        // Error handled by component
      });
    }
  }, [id, duplicateId, isEditMode, loadDirectDial, location.key, location.pathname, location.search]);

  return {
    formData,
    setFormData,
    isLoading,
  };
}

